<template>
  <lb-dialog
    v-model="internalValue"
    heading="Net Sales Breakup"
    :width="width"
    :loading="loading"
  >
    <template v-slot:body>
      <div>
        <div class="d-flex align-center mb-4">
          <v-btn-toggle
            v-if="hasBreakdown"
            v-model="viewMode"
            mandatory
            dense
            class="elevation-0"
            color="primary"
          >
            <v-btn value="grouped"><span>Grouped</span></v-btn>
            <v-btn value="ungrouped"><span>Ungrouped</span></v-btn>
          </v-btn-toggle>
          <v-spacer />
        </div>
        <div class="d-flex" v-if="showUploadButton">
          <v-btn color="secondaryBtn" class="mb-2" @click="emitOpenUpload()"><span>Upload Commentary</span></v-btn>
        </div>
        <div v-if="showGroupedTable" class="mb-4 table-wrapper">
          <div style="max-height:400px;overflow-y:auto;">
            <v-data-table
              :headers="clientTypeHeaders"
              :items="breakdownData"
              item-key="_id"
              class="elevation-1"
              fixed
              :loading="loading"
              hide-default-footer
              fixed-header
              height="400px"
            >
              <template v-slot:item="{ item }">
                <tr
                  @click="item._id !== total ? handleToggleGroup(item._id) : null"
                  class="parent-row"
                  v-if="isParent(item)"
                >
                  <td>
                    <div class="d-flex align-center">
                      <v-btn icon v-if="showToggleIcon(item)" class="toggle-btn mr-2">
                        <v-icon>{{ expandedGroups.includes(item._id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      </v-btn>
                      <span :class="item._id === total || (isSpecialRu && item._id === local) ? 'ml-10' : ''">
                        {{ getItemId(item) }}
                      </span>
                    </div>
                  </td>
                  <td>{{ getSubtotalValue(item, 'cl_code', '') }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.actual_amount) }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.proforma_amount) }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.actual_vs_proforma_variance_amount) }}</td>
                  <td>{{ getSubtotalValue(item, 'commentary1', '-') }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.forecast_amount) }}</td>
                  <td>{{ $nova.formatNumber(item.subtotal.actual_vs_forecast_variance_amount) }}</td>
                  <td>{{ getSubtotalValue(item, 'commentary2', '-') }}</td>
                </tr>
                <template v-if="isExpanded(item)">
                  <tr v-for="client in item.documents" :key="client._id" class="child-row">
                    <td><div class="pl-8">{{ getClientName(client) }}</div></td>
                    <td>{{ getClientValue(client, 'cl_code', '') }}</td>
                    <td>{{ $nova.formatNumber(client.actual_amount) }}</td>
                    <td>{{ $nova.formatNumber(client.proforma_amount) }}</td>
                    <td>{{ $nova.formatNumber(client.actual_vs_proforma_variance_amount) }}</td>
                    <td>{{ getClientValue(client, 'commentary1', '-') }}</td>
                    <td>{{ $nova.formatNumber(client.forecast_amount) }}</td>
                    <td>{{ $nova.formatNumber(client.actual_vs_forecast_variance_amount) }}</td>
                    <td>{{ getClientValue(client, 'commentary2', '-') }}</td>
                  </tr>
                </template>
              </template>
            </v-data-table>
          </div>
        </div>
        <div v-else class="mb-4 table-wrapper-ungrouped">
          <lb-datatablenew
            v-if="clientByClientHasData"
            :headers="clientByClientHeaders"
            :items="clientbyclientData"
            :loading="loading"
            hide-default-footer
            item-key="_id"
            class="elevation-1"
            fixed-header
            height="auto"
          ></lb-datatablenew>
        </div>
      </div>
    </template>
  </lb-dialog>
</template>

<script>

 export default {
  name: 'NetSalesBreakupDialog',
  props: {
    value: { type: Boolean, default: false },          // REQUIRED for v-model
    loading: { type: Boolean, default: false },
    width: { type: [String, Number], default: 1000 },
    breakdownData: { type: Array, default: () => [] },
    netSalesViewMode: { type: String, default: 'grouped' },
    expandedGroups: { type: Array, default: () => [] },
    clientbyclientData: { type: Array, default: () => [] },
    clientbyclientFullData: { type: Array, default: () => [] },
    clientTypeHeaders: { type: Array, default: () => [] },
    clientByClientHeaders: { type: Array, default: () => [] },
    isPreparer: { type: Boolean, default: false },
    isPendingLocal: { type: Boolean, default: false },
    isPendingRegional: { type: Boolean, default: false },
    isSpecialRu: { type: Boolean, default: false }
  },
  data() {
    return {
      total: 'Total',
      local: 'Local'
    };
  },
  computed: {
    internalValue: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    viewMode: {
      get() { return this.netSalesViewMode },
      set(v) { this.$emit('update:netSalesViewMode', v) }
    },
    hasBreakdown() {
      return this.breakdownData && this.breakdownData.length > 0;
    },
    showUploadButton() {
      return this.isPreparer && (this.isPendingLocal || this.isPendingRegional);
    },
    showGroupedTable() {
      return this.viewMode === 'grouped' && this.hasBreakdown;
    },
    clientByClientHasData() {
      return this.clientbyclientData && this.clientbyclientData.length > 0;
    }
  },
  methods: {
    emitToggle(group) {
      this.$emit('toggle-revenue-group', group);
    },
    changeViewMode(mode) {
      this.$emit('update:netSalesViewMode', mode);
    },
    emitOpenUpload() {
      this.$emit('open-upload-commentary');
    },
    handleToggleGroup(groupId) {
      this.$emit('toggle-group', groupId);
    },
    isParent(item) {
      // Check if item is a parent row (has subtotal or documents)
      return item && (item.subtotal || item.documents);
    },
    showToggleIcon(item) {
      // Show toggle icon if item has documents (children)
      return item && item.documents && item.documents.length > 0 && item._id !== this.total;
    },
    isExpanded(item) {
      // Check if item is expanded
      return item && this.expandedGroups && this.expandedGroups.includes(item._id);
    },
    // Helper methods to replace optional chaining and nullish coalescing in template
    getItemId(item) {
      if (item && item._id) return item._id;
      if (item && item.subtotal && item.subtotal.cl_code) return item.subtotal.cl_code;
      return '-';
    },
    getSubtotalValue(item, key, defaultValue) {
      if (item && item.subtotal && item.subtotal[key] != null) {
        return item.subtotal[key];
      }
      return defaultValue;
    },
    getClientName(client) {
      if (client && client.client_name) return client.client_name;
      if (client && client.cl_code) return client.cl_code;
      return '-';
    },
    getClientValue(client, key, defaultValue) {
      if (client && client[key] != null) {
        return client[key];
      }
      return defaultValue;
    }
  }
};
</script>
